"use client"

import React, { useRef } from "react"
import cx from "classnames"

import { Icon } from "components/ui"
import OddsValue from "compositions/OddsValue/OddsValue"
import { useDetailedSxBetslip } from "@/contexts/DetailedSxBetslipContext"
import { sendGTMEvent } from "@next/third-parties/google"
import useOddsChange from "src/hooks/useOddsChange"
import { createSlug } from "@/SX/utils/transformSxMarkets"

type SXOutcomeButtonProps = {
  marketName: string
  order: any
  game: any
  isLocked: boolean
  size?: 28 | 40
  section?: string
}

const SXOutcomeButton: React.FC<SXOutcomeButtonProps> = props => {
  const { marketName, order, game, isLocked, size = 28, section } = props

  const { key, label, odds, order: orderData, outcome } = order

  const { marketHash, baseToken, bestOddsOutcomeOne, bestOddsOutcomeTwo } =
    orderData.market
  const nodeRef = useRef<HTMLDivElement>(null)

  const isMakerBettingOutcomeOne = outcome === "one" ? false : true
  const percentageOdds = isMakerBettingOutcomeOne
    ? bestOddsOutcomeTwo
    : bestOddsOutcomeOne
  // Use SX betslip context instead of generic useButton
  const { ticket, addToTicket, removeFromTicket } = useDetailedSxBetslip()

  // Add odds change effect for color/icon animation
  useOddsChange({ odds: parseFloat(odds), nodeRef })

  // Check if this selection is already in the ticket using uniqueId for UI state
  const isActive = ticket.some(item => item.uniqueId === key)
  const isButtonDisabled = isLocked || !odds

  const handleClick = () => {
    const selected = isActive

    const sxTicketItem = {
      marketHash: marketHash,
      outcome: label,
      percentageOdds,
      outcomeOneName: orderData.outcomeOneName,
      outcomeTwoName: orderData.outcomeTwoName,
      isMakerBettingOutcomeOne,
      marketName: marketName,
      game: {
        ...game,
        leagueSlug: createSlug(game.leagueLabel),
      },
      odds,
      uniqueId: key,
      baseToken: baseToken,
      gameLabel: game.gameLabel || game.title
    }

    const eventPayload = {
      event: "odds_card_clicked",
      selected,
      section: section || "SX",
      sports_name: game?.sport?.name,
      league_name: game?.league?.leagueLabel,
      game_name: label,
      market_name: marketName,
      game_id: game?.gameId,
    }

    if (selected) {
      // Remove from ticket if already present
      removeFromTicket(key)
    } else {
      addToTicket(sxTicketItem)
    }

    sendGTMEvent(eventPayload)
  }

  const buttonClassName = cx(
    "group/button w-full relative flex items-center justify-between ds:px-3 mb:px-2 overflow-hidden truncate",
    "text-caption-13 font-medium border-none rounded-min select-none h-7",
    {
      "hover:text-brand-light-500 hover:bg-brand-900":
        !isButtonDisabled && !isActive,
      "text-black bg-brand-light-200": isActive,
      "bg-accent-grey-800": !isActive,
      "text-accent-grey-200 cursor-not-allowed": isButtonDisabled,
      "h-7": size === 28,
      "h-10": size === 40,
    }
  )

  const titleClassName = cx("text-left truncate", {
    "group-hover/button:text-brand-light-500": !isButtonDisabled && !isActive,
    "text-black": isActive,
    "text-accent-grey-200": !isActive || isButtonDisabled,
  })
  const oddsClassName = cx("group/odds flex items-center")
  const arrowClassName = cx(
    "size-4 text-transparent transition-color",
    "group-[.increased]/odds:text-accent-green-700",
    "group-[.decreased]/odds:text-accent-red-700 group-[.decreased]/odds:rotate-180"
  )
  const valueClassName = cx(
    "transition-color",
    "group-[.increased]/odds:text-accent-green-700",
    "group-[.decreased]/odds:text-accent-red-700"
  )

  return (
    <button
      className={buttonClassName}
      disabled={isButtonDisabled}
      onClick={handleClick}
    >
      <div className="flex items-center truncate">
        {isButtonDisabled && (
          <Icon
            className="mr-1 size-4 text-accent-grey-400"
            name="interface/lock"
          />
        )}
        <div className={titleClassName}>{order.label}</div>
      </div>
      <div ref={nodeRef} className={oddsClassName}>
        <Icon className={arrowClassName} name="interface/caret_up" />
        <OddsValue className={valueClassName} odds={order.odds} />
      </div>
    </button>
  )
}

export default SXOutcomeButton
